import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRouter } from '@tanstack/react-router';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

interface AdminPageLayoutProps {
  title: string;
  description?: string;
  icon?: React.ElementType;
  children: ReactNode;
  showBackButton?: boolean;
  backPath?: string;
  backLinkText?: string;
  headerActions?: ReactNode;
  className?: string;
  badgeText?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  isSuperAdminPage?: boolean;
}

export function AdminPageLayout({
  title,
  description,
  icon: Icon,
  children,
  showBackButton = true,
  backPath = '/admin',
  backLinkText = 'Back to Admin Hub',
  headerActions,
  className,
  badgeText = 'Admin',
  badgeVariant = 'destructive',
  isSuperAdminPage = false,
}: AdminPageLayoutProps) {
  const router = useRouter();

  const handleGoBack = () => {
    router.navigate({ to: backPath });
  };

  return (
    <div className={cn('flex min-h-svh flex-col gap-6 p-4 lg:p-6', className)}>
      <div className="w-full max-w-[1400px] mx-auto space-y-6">
        {/* Super Admin Banner */}
        {isSuperAdminPage && (
          <div className="w-full bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center justify-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              <span className="font-semibold">Super Administrator Access</span>
              <span className="text-red-200">•</span>
              <span className="text-sm">{title}</span>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="space-y-4">
          {/* Navigation */}
          {showBackButton && (
            <div className="flex items-center">
              <Button variant="ghost" size="sm" onClick={handleGoBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {backLinkText}
              </Button>
            </div>
          )}

          {/* Page Title */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                {Icon && <Icon className="h-8 w-8 text-primary" />}
                <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h1>
                {!isSuperAdminPage && (
                  <Badge variant={badgeVariant} className="ml-2">
                    {badgeText}
                  </Badge>
                )}
              </div>
              {description && <p className="text-muted-foreground md:text-base">{description}</p>}
            </div>
            {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
          </div>
        </div>

        {/* Page Content */}
        {children}
      </div>
    </div>
  );
}
